/**
 * AgingPage — accounts-receivable aging report (Phase 12 / Task 12.4).
 *
 * Reads GET /v1/billing/aging: SENT + unpaid invoices bucketed current / 31-60 /
 * 61-90 / 90+ by the user's LOCAL day (DEC-066), per operator + tenant totals.
 * Read-only + a client-side CSV export of the same data. Money via
 * formatUsdFromCents. VIEW_INVOICES is enforced by the API (three-layer security).
 */

import React from 'react';
import { Download } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { SortableHead } from 'src/components/ui/sortable-head';

import { formatUsdFromCents } from '../lib/money';
import { agingToCsv, downloadFile } from '../lib/aging-csv';
import { sortRows, useUrlSort } from '../lib/url-state';
import { useAging, type AgingOperator } from '../features/billing/hooks';

/** The columns the aging table sorts by (OPEN-204); anything else in ?sort= is ignored. */
const AGING_SORTS = ['operator', 'current', 'd31_60', 'd61_90', 'd90plus', 'total'] as const;

export function AgingPage(): React.JSX.Element {
  const { data, isLoading, isError, refetch } = useAging();

  // OPEN-204 — click a header to sort the rows; the totals row and the CSV keep the API's order.
  const { sort, toggle } = useUrlSort(AGING_SORTS, null, []);
  const operators: readonly AgingOperator[] = sortRows(data?.operators ?? [], sort, (o, k) => {
    switch (k) {
      case 'operator':
        return o.operatorName;
      case 'current':
        return o.current;
      case 'd31_60':
        return o.d31_60;
      case 'd61_90':
        return o.d61_90;
      case 'd90plus':
        return o.d90plus;
      case 'total':
        return o.total;
    }
  });
  const totals = data?.totals;

  const handleExport = React.useCallback((): void => {
    if (data === undefined) return;
    downloadFile(
      'aging-report.csv',
      agingToCsv(data.operators, data.totals),
      'text/csv;charset=utf-8',
    );
  }, [data]);

  return (
    <div style={{ padding: '24px', maxWidth: '1080px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          marginBottom: '16px',
          flexWrap: 'wrap',
        }}
      >
        <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
          Outstanding SENT invoices, aged by your local day
          {data?.timezone !== undefined ? ` (${data.timezone})` : ''}.
        </p>
        <Button
          variant="outline"
          onClick={handleExport}
          disabled={operators.length === 0}
          title="Download this aging report as CSV"
        >
          <Download size={14} aria-hidden="true" style={{ marginRight: '6px' }} />
          Export CSV
        </Button>
      </div>

      {isLoading && (
        <p role="status" style={{ color: 'var(--color-text-secondary)' }}>
          Loading aging report…
        </p>
      )}

      {isError && (
        <div style={{ color: 'var(--color-danger)' }}>
          <p style={{ marginBottom: '8px' }}>Couldn&apos;t load the aging report.</p>
          <Button
            variant="outline"
            onClick={(): void => {
              void refetch();
            }}
          >
            Retry
          </Button>
        </div>
      )}

      {!isLoading && !isError && operators.length === 0 && (
        <p style={{ color: 'var(--color-text-secondary)' }}>
          No outstanding invoices. Nothing is aged.
        </p>
      )}

      {!isLoading && !isError && operators.length > 0 && totals !== undefined && (
        <div
          style={{
            borderRadius: 'var(--radius-card)',
            backgroundColor: 'var(--color-bg-secondary)',
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHead column="operator" sort={sort} onSort={toggle}>
                  Operator
                </SortableHead>
                <SortableHead className="text-right" column="current" sort={sort} onSort={toggle}>
                  Current
                </SortableHead>
                <SortableHead className="text-right" column="d31_60" sort={sort} onSort={toggle}>
                  31–60
                </SortableHead>
                <SortableHead className="text-right" column="d61_90" sort={sort} onSort={toggle}>
                  61–90
                </SortableHead>
                <SortableHead className="text-right" column="d90plus" sort={sort} onSort={toggle}>
                  90+
                </SortableHead>
                <SortableHead className="text-right" column="total" sort={sort} onSort={toggle}>
                  Total
                </SortableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {operators.map((o) => {
                const key = o.operatorId ?? `external-${o.operatorName ?? 'unknown'}`;
                return (
                  <TableRow key={key}>
                    <TableCell>
                      {o.operatorName ?? (
                        <span style={{ color: 'var(--color-text-tertiary)' }}>
                          Unmapped operator
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{formatUsdFromCents(o.current)}</TableCell>
                    <TableCell className="text-right">{formatUsdFromCents(o.d31_60)}</TableCell>
                    <TableCell className="text-right">{formatUsdFromCents(o.d61_90)}</TableCell>
                    <TableCell className="text-right">{formatUsdFromCents(o.d90plus)}</TableCell>
                    <TableCell className="text-right" style={{ fontWeight: 600 }}>
                      {formatUsdFromCents(o.total)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell style={{ fontWeight: 600 }}>Total</TableCell>
                <TableCell className="text-right">{formatUsdFromCents(totals.current)}</TableCell>
                <TableCell className="text-right">{formatUsdFromCents(totals.d31_60)}</TableCell>
                <TableCell className="text-right">{formatUsdFromCents(totals.d61_90)}</TableCell>
                <TableCell className="text-right">{formatUsdFromCents(totals.d90plus)}</TableCell>
                <TableCell className="text-right" style={{ fontWeight: 700 }}>
                  {formatUsdFromCents(totals.total)}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      )}
    </div>
  );
}
